import React, { forwardRef } from 'react';
import { cn } from '../../../utils/cn';

/**
 * GlassSurface - Base glass morphism container component
 *
 * The foundational building block for all glass components. Provides
 * configurable glass effects, blur intensity, and interactive states.
 *
 * @example
 * ```tsx
 * <GlassSurface variant="default" blur="strong" glow="blue">
 *   <Content />
 * </GlassSurface>
 * ```
 */

export interface GlassSurfaceProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Glass intensity variant */
  variant?: 'default' | 'strong' | 'subtle' | 'elevated';
  /** Blur intensity */
  blur?: 'subtle' | 'base' | 'strong' | 'intense';
  /** Border radius size */
  rounded?: 'none' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  /** Enable hover lift effect */
  hover?: boolean;
  /** Enable glow effect on hover */
  glow?: 'none' | 'blue' | 'violet' | 'emerald' | 'amber' | 'rose';
  /** Enable press/active effect */
  press?: boolean;
  /** Internal padding */
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Show border */
  bordered?: boolean;
  /** Custom element tag */
  as?: keyof JSX.IntrinsicElements;
  /** Additional CSS classes */
  className?: string;
  /** Surface content */
  children: React.ReactNode;
}

const variantClasses = {
  default: 'bg-[var(--glass)] backdrop-blur-[var(--blur-strong)]',
  strong: 'bg-[var(--glass-strong)] backdrop-blur-[var(--blur-intense)]',
  subtle: 'bg-[var(--glass-subtle)] backdrop-blur-[var(--blur-base)]',
  elevated: 'bg-[var(--glass-strong)] backdrop-blur-[var(--blur-intense)] shadow-[var(--shadow-strong)]',
};

const blurClasses = {
  subtle: 'backdrop-blur-[8px]',
  base: 'backdrop-blur-[12px]',
  strong: 'backdrop-blur-[20px]',
  intense: 'backdrop-blur-[24px]',
};

const roundedClasses = {
  none: 'rounded-none',
  sm: 'rounded-lg',
  md: 'rounded-xl',
  lg: 'rounded-2xl',
  xl: 'rounded-3xl',
  '2xl': 'rounded-[24px]',
  full: 'rounded-full',
};

const paddingClasses = {
  none: '',
  xs: 'p-2',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
  xl: 'p-8',
};

const glowClasses = {
  none: '',
  blue: 'glass-glow',
  violet: 'glass-glow-violet',
  emerald: 'glass-glow-emerald',
  amber: 'hover:shadow-[var(--glow-amber)]',
  rose: 'hover:shadow-[var(--glow-rose)]',
};

/**
 * GlassSurface Component
 *
 * Features:
 * - Four glass intensity variants
 * - Configurable blur intensity
 * - Expressive hover and press states
 * - Optional glow effects
 * - Polymorphic component (as prop)
 * - Theme-aware (light/dark mode)
 */
export const GlassSurface = forwardRef<HTMLDivElement, GlassSurfaceProps>(
  (
    {
      variant = 'default',
      blur,
      rounded = 'lg',
      hover = false,
      glow = 'none',
      press = false,
      padding = 'none',
      bordered = true,
      as: Component = 'div',
      className,
      children,
      ...props
    },
    ref
  ) => {
    const surfaceClasses = cn(
      // Base glass styles
      variantClasses[variant],
      // Override blur if specified
      blur && blurClasses[blur],
      // Border radius
      roundedClasses[rounded],
      // Padding
      paddingClasses[padding],
      // Border
      bordered && 'border border-[var(--glass-border)]',
      // Shadow (if not elevated)
      variant !== 'elevated' && 'shadow-[var(--shadow-soft)]',
      // Interactive states
      hover && 'glass-hover-lift cursor-pointer',
      glow !== 'none' && glowClasses[glow],
      press && 'glass-press',
      // Transition
      'transition-all duration-200',
      // Custom classes
      className
    );

    const ElementComponent = Component as React.ElementType;

    return (
      <ElementComponent ref={ref} className={surfaceClasses} {...props}>
        {children}
      </ElementComponent>
    );
  }
);

GlassSurface.displayName = 'GlassSurface';
